import { Box, Link } from '@chakra-ui/react';
import { Text } from '@mercator-gcp/grout';
import { EmptyField } from 'components/elements/EmptyField';
import { Label } from 'components/elements/Label';
import { SkeletonProps } from 'components/elements/Skeleton';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';

import { InfoModal } from 'components/widgets/InfoModal';
import { useEventTriggerCategories } from 'state/eventTriggers';
import { LandTitle } from 'types/LandTitle';
import { DOCUMENT_IDS } from 'utils/constants';
import { City } from 'utils/constants/location_legacy';
import { formatPrice } from 'utils/currency';
import { formatDate } from 'utils/date';
import { capitalizeWords } from 'utils/format';
import { cityMap } from 'utils/misc';

type Props = {
  result: LandTitle;
  skeleton: (props: Omit<SkeletonProps, 'isLoaded'>) => JSX.Element;
};

const capitalizationStyle = {
  textTransform: 'capitalize',
};

const LandTitleMaterials = ({ result, skeleton: Skeleton }: Props) => {
  const { data: allCategories } = useEventTriggerCategories(
    [cityMap[City.AUSTIN]],
    [DOCUMENT_IDS.LAND_TITLE],
  );
  const categoryName = allCategories?.find(
    (category) => category.id === result?.category_id,
  )?.name;

  return (
    <Box>
      <InfoModal.Row>
        <Label>Category</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text">
            {categoryName ? (
              <Text sx={capitalizationStyle}>{categoryName}</Text>
            ) : (
              <EmptyField />
            )}{' '}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Date</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={8}>
            {result?.issueddate ? (
              <Text>{formatDate(result?.issueddate)}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Proposed Use</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={8}>
            {result?.property_use ? (
              <Text sx={capitalizationStyle}>{result?.property_use}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Assessment Value</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={8}>
            {result?.property_value ? (
              <Text>{formatPrice(result?.property_value)}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Unit Number</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={8}>
            {result?.parcelnum ? (
              <Text sx={capitalizationStyle}>{result?.parcelnum}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Land Owner</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={8}>
            <FlexColumn>
              {result.companies.length > 0 ? (
                result.companies.map((company) => {
                  if (company.name) {
                    const companyName = capitalizeWords(company.name);

                    return company.uuid ? (
                      <Link href={`/companies/${company.uuid}`} key={company.uuid}>
                        <Text.BodyBold>{companyName}</Text.BodyBold>
                      </Link>
                    ) : (
                      <Text.Body key={company.name}>{companyName}</Text.Body>
                    );
                  }

                  return null;
                })
              ) : (
                <EmptyField />
              )}
            </FlexColumn>
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Owner Address</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={8}>
            {result?.buyer_mail_address_full ? (
              <Text sx={capitalizationStyle}>{result?.buyer_mail_address_full}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
      <InfoModal.Row>
        <Label>Title Number</Label>
        <InfoModal.RowDetails>
          <Skeleton variant="text" noOfLines={8}>
            <Text>{result?.title_number} </Text>
          </Skeleton>
        </InfoModal.RowDetails>
      </InfoModal.Row>
    </Box>
  );
};

export default LandTitleMaterials;
